// The Station scope selector (CTV-759 / ADR-0036).
//
// The single control that sets Station scope. It sits next to each page's
// heading, NOT in the sidebar, and the placement is load-bearing rather than
// cosmetic: the control it replaces (StationSwitcher) lived among navigation
// links and was a NAVIGATION control — picking a station rewrote the URL and
// took you elsewhere. This is a FILTER control: it states what the content
// below it covers.
//
// On a scope-flexible tab it offers All stations plus each station. On a
// station-bound tab it offers only the stations, so a page that cannot show
// all stations simply renders a shorter list — no disabled item, no hidden
// nav, no modal.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { ALL_STATIONS_SLUG, rememberAdminScope } from './lastAdminStation';
import { withAdminSlug } from './adminScopeTabs';

export interface StationScopeSelectorProps {
  /** Whether this page offers All stations. */
  scopeMode: 'flexible' | 'bound';
  /** The scope currently in effect: a station slug, or `all`. */
  currentSlug: string;
}

/** Query params that name a station's own records; they do not carry across a switch. */
const STATION_BOUND_PARAMS = ['still', 'promo'];
function carriedSearch(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of STATION_BOUND_PARAMS) params.delete(key);
  const out = params.toString();
  return out ? `?${out}` : '';
}

const StationScopeSelector: React.FC<StationScopeSelectorProps> = ({
  scopeMode,
  currentSlug,
}) => {
  const { myAdminStations } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const stations = (myAdminStations ?? []).filter((s) => s.slug);

  // Nothing to choose between. Also covers the pre-fetch state, so the
  // control never flashes in and out on a solo-station admin's page.
  if (stations.length <= 1) return null;

  const options: Array<{ value: string; label: string }> = [
    ...(scopeMode === 'flexible'
      ? [{ value: ALL_STATIONS_SLUG, label: 'All stations' }]
      : []),
    ...stations.map((s) => ({
      value: s.slug as string,
      label: s.displayName || s.name,
    })),
  ];

  const current = options.find((o) => o.value === currentSlug);

  const select = (value: string) => {
    setOpen(false);
    if (value === currentSlug) return;
    rememberAdminScope(value);
    // Swap the slug segment so the tab is preserved:
    // /admin/<old>/submissions → /admin/<new>/submissions.
    // The query string comes along too (CTV-840): Materials keeps the chosen
    // material there, so switching station shows the same piece for the new
    // station rather than dropping the reader back on the gallery. Anything
    // that names one station's own records (a saved TV-screen still, a promo
    // code) is dropped: the new station has its own.
    navigate(withAdminSlug(location.pathname, value) + carriedSearch(location.search));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Station scope"
        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors"
      >
        <span className="truncate max-w-[200px]">{current?.label ?? 'Select station'}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-20 mt-1 min-w-[220px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-xl"
        >
          {options.map((o) => {
            const isCurrent = o.value === currentSlug;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => select(o.value)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-left hover:bg-slate-50 ${
                  isCurrent ? 'bg-slate-50 font-medium' : ''
                }`}
              >
                <span className="truncate">{o.label}</span>
                {isCurrent && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StationScopeSelector;
